import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppRateForm, { AppRateFormItem } from '@nxsys/components/AppRateForm';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import AppSwitch from '@nxsys/components/AppSwitch';
import AppUploadFile from '@nxsys/components/AppUploadFile';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Option } from '@nxsys/types/models/Common';
import { countryList } from '@nxsys/utils';
import {
  formatPostCode,
  postCodeValidationRule,
} from '@nxsys/utils/formFieldValidation';
import type { FormProps } from 'antd';
import { Col, Form, Row, Typography } from 'antd';
import React, {
  Ref,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

const { Text } = Typography;

export type AssignmentFormValues = {
  assignmentName?: string;
  projectRef?: string;
  clientRef?: string;
  status?: Option<string> | null;
  siteId?: Option<string> | null;
  employeeAndSubcontractor?: any;
  agencyName?: any;
  startDate?: string;
  endDate?: string;
  invoicingAddress?: {
    address1?: string;
    address2?: string;
    address3?: string;
    city?: string;
    county?: string;
    postCode?: string;
    country?: Option<string> | null;
  };
  workLocation?: AssignmentFormValues['invoicingAddress'];
  rates?: Array<Partial<AppRateFormItem>>;
  attachment?: File | string | null;
};

export type AssignmentFormRef = {
  submit: () => void;
};

type AssignmentFormProps = {
  defaultValues?: Partial<AssignmentFormValues>;
  formRef?: Ref<AssignmentFormRef>;
  onSubmit?: (values: AssignmentFormValues) => void;
  submitting?: boolean;
  hideAgencyField?: boolean;
};

const innerDefaultValues = {
  assignmentName: '',
  projectRef: '',
  clientRef: '',
  status: { label: 'ACTIVE', value: 'ACTIVE' },
  siteId: null,
  employeeAndSubcontractor: null,
  agencyName: null,
  startDate: '',
  endDate: '',
  invoicingAddress: {
    address1: '',
    address2: '',
    address3: '',
    city: '',
    county: '',
    postCode: '',
    country: null,
  },
  workLocation: {
    address1: '',
    address2: '',
    address3: '',
    city: '',
    county: '',
    postCode: '',
    country: null,
  },
  rates: [
    {
      rateCode: null,
      rateType: '',
      description: '',
      rate: '',
      vatDetails: null,
      vatRate: '',
    },
  ],
  attachment: null,
};

/**
 * Stacked-layout clone of `AssignmentForm`:
 * - Keeps the same form logic & fields
 * - Uses AntD `layout="vertical"` so labels render above inputs
 */
const AssignmentFormStacked: React.FC<AssignmentFormProps> = ({
  defaultValues = innerDefaultValues,
  onSubmit,
  formRef,
  hideAgencyField = false,
}: AssignmentFormProps) => {
  const { payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;

  const { messages } = useTypedIntl();
  const [form] = Form.useForm<AssignmentFormValues>();
  const [sameAsInvoicing, setSameAsInvoicing] = useState(true);
  const watchInvoicingAddress = Form.useWatch('invoicingAddress', form);
  const watchValues = Form.useWatch([], form);

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
  }));

  const statusOptions = useMemo(
    () => [
      { label: messages['common.active'], value: 'ACTIVE' },
      { label: messages['common.inactive'], value: 'INACTIVE' },
    ],
    [messages],
  );

  const onFinish: FormProps<AssignmentFormValues>['onFinish'] = (values) => {
    const mergedValues: AssignmentFormValues = {
      ...defaultValues,
      ...values,
      workLocation: sameAsInvoicing
        ? values.invoicingAddress
        : values.workLocation,
    };
    onSubmit?.(mergedValues);
  };

  const onFinishFailed: FormProps<AssignmentFormValues>['onFinishFailed'] = (
    errorInfo,
  ) => {
    console.log('Failed:', errorInfo);
  };

  useEffect(() => {
    if (sameAsInvoicing) {
      form.setFieldsValue({
        workLocation: watchInvoicingAddress,
      });
    }
  }, [watchInvoicingAddress, sameAsInvoicing]);

  const handleSelectAgency = (value: any) => {
    const agencyData = value?.fullData as IAgency;
    form.setFieldsValue({
      agencyName: value,
      invoicingAddress: {
        address1: agencyData?.address?.[0] || '',
        address2: agencyData?.address?.[1] || '',
        address3: agencyData?.address?.[2] || '',
        city: agencyData?.city || '',
        county: agencyData?.county || '',
        postCode: agencyData?.postCode || '',
        country: agencyData?.country
          ? countryList.find((c) => c.value === agencyData.country)
          : undefined,
      },
    });
  };

  return (
    <div>
      <div className="rounded-lg bg-white px-4 py-4 flex items-center flex-col">
        <div className="max-w-[600px]">
          <div className="px-4 pt-2">
            <Typography.Text className="font-medium text-gray-800 text-base">
              {messages['assignment.section.detail'] ?? 'Assignment Detail'}
            </Typography.Text>
          </div>
          <AppFormContainer
            form={form}
            layout="vertical"
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            initialValues={{
              ...innerDefaultValues,
              ...defaultValues,
            }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            className="px-4 py-2"
          >
            <Form.Item
              label={messages['assignment.form.assignmentName']}
              name="assignmentName"
              rules={[
                {
                  required: true,
                  message: messages['form.pleaseInput'],
                },
              ]}
            >
              <AppInput
                placeholder={messages['assignment.form.enterAssignmentName']}
              />
            </Form.Item>

            {!isIreland && !hideAgencyField && (
              <Form.Item
                label={messages['assignment.form.agencyName']}
                name="agencyName"
                rules={[{ required: true }]}
              >
                <AppSelectSync
                  placeholder={messages['assignment.form.selectAgency']}
                  apiType="searchAgency"
                  type="agency"
                  onChangeValue={handleSelectAgency}
                />
              </Form.Item>
            )}

            <Form.Item
              label={messages['common.employee']}
              name="employeeAndSubcontractor"
              rules={[{ required: true }]}
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <AppSelectSync
                placeholder={messages['form.selectEmployeeAndSubcontractor']}
                apiType="searchEmployeeSubcontractV2"
                type="emp&sub"
                onChangeValue={(value: any) => {
                  form.setFieldsValue({ employeeAndSubcontractor: value });
                }}
              />
            </Form.Item>
            <div className="flex flex-wrap gap-4">
              <div className="w-full md:w-[calc(50%-8px)]">
                <Form.Item
                  label={messages['assignment.form.clientRef']}
                  name="clientRef"
                >
                  <AppInput
                    placeholder={messages['assignment.form.enterClientRef']}
                  />
                </Form.Item>
              </div>

              {!isIreland && (
                <div className="w-full md:w-[calc(50%-8px)]">
                  <Form.Item
                    label={messages['assignment.form.siteId']}
                    name="siteId"
                  >
                    <AppSelectSync
                      placeholder={messages['assignment.form.selectSiteId']}
                      apiType="searchSiteInAgency"
                      type="agencySite"
                      onChangeValue={(value: any) => {
                        form.setFieldsValue({ siteId: value });
                      }}
                      searchId={watchValues?.agencyName?.value}
                    />
                  </Form.Item>
                </div>
              )}

              <div className="w-full md:w-[calc(50%-8px)]">
                <Form.Item
                  label={messages['assignment.form.projectRef']}
                  name="projectRef"
                >
                  <AppInput
                    placeholder={messages['assignment.form.enterProjectRef']}
                  />
                </Form.Item>
              </div>

              <div className="w-full md:w-[calc(50%-8px)]">
                <Form.Item
                  label={messages['assignment.form.status']}
                  name="status"
                  rules={[{ required: true }]}
                >
                  <AppSelect
                    options={statusOptions}
                    placeholder={messages['assignment.form.selectStatus']}
                  />
                </Form.Item>
              </div>

              <div className="w-full md:w-[calc(50%-8px)]">
                <Form.Item
                  label={messages['assignment.form.startDate']}
                  name="startDate"
                  rules={[{ required: true }]}
                >
                  <AppDatePicker
                    placeholder={messages['assignment.form.selectDate']}
                    format={'DD/MM/YYYY'}
                  />
                </Form.Item>
              </div>

              <div className="w-full md:w-[calc(50%-8px)]">
                <Form.Item
                  label={messages['assignment.form.endDate']}
                  name="endDate"
                >
                  <AppDatePicker
                    placeholder={messages['assignment.form.selectDate']}
                    format={'DD/MM/YYYY'}
                  />
                </Form.Item>
              </div>

              <div className="w-full">
                <Form.Item
                  label={messages['common.attachment']}
                  name="attachment"
                >
                  <AppUploadFile
                    urlFile={
                      typeof watchValues?.attachment === 'string'
                        ? watchValues?.attachment
                        : undefined
                    }
                    onChange={(file: File) => {
                      form.setFieldsValue({ attachment: file });
                    }}
                  />
                </Form.Item>
              </div>
            </div>
          </AppFormContainer>

          <hr className="mx-4 my-4 border-gray-200" />

          {!isIreland && (
            <>
              <div className="px-4 pt-4">
                <Typography.Text className="font-medium text-gray-800 text-base">
                  {messages['assignment.detail.invoicingAddress'] ??
                    'Invoicing Address'}
                </Typography.Text>
              </div>
              <AppFormContainer
                form={form}
                layout="vertical"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                initialValues={defaultValues}
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                className="px-4 py-2"
              >
                <Row gutter={[16, 12]}>
                  <Col span={24}>
                    <Form.Item
                      label={messages['assignment.address.address1']}
                      name={['invoicingAddress', 'address1']}
                    >
                      <AppInput
                        placeholder={
                          messages['assignment.address.enterAddress']
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      label={messages['assignment.address.address2']}
                      name={['invoicingAddress', 'address2']}
                    >
                      <AppInput
                        placeholder={
                          messages['assignment.address.enterAddress']
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      label={messages['assignment.address.address3']}
                      name={['invoicingAddress', 'address3']}
                    >
                      <AppInput
                        placeholder={
                          messages['assignment.address.enterAddress']
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24} md={14}>
                    <Form.Item
                      label={messages['assignment.address.city']}
                      name={['invoicingAddress', 'city']}
                    >
                      <AppInput
                        placeholder={messages['assignment.address.enterCity']}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24} md={10}>
                    <Form.Item
                      label={messages['assignment.address.county']}
                      name={['invoicingAddress', 'county']}
                    >
                      <AppInput
                        placeholder={
                          messages['assignment.address.selectCounty']
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24} md={14}>
                    <Form.Item
                      label={messages['assignment.address.postCode']}
                      name={['invoicingAddress', 'postCode']}
                      rules={[
                        postCodeValidationRule(messages, payrollCountry),
                      ]}
                    >
                      <AppInput
                        placeholder={
                          messages['assignment.address.enterPostCode']
                        }
                        upperCaseAll
                        onChange={(e) => {
                          form.setFieldValue(
                            ['invoicingAddress', 'postCode'],
                            formatPostCode(e.target.value, payrollCountry),
                          );
                        }}
                        maxLength={8}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24} md={10}>
                    <Form.Item
                      label={messages['assignment.address.country']}
                      name={['invoicingAddress', 'country']}
                    >
                      <AppSelect
                        placeholder={
                          messages['assignment.address.selectCountry']
                        }
                        options={countryList}
                        className="w-full !min-w-0"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </AppFormContainer>

              <hr className="mx-4 my-4 border-gray-200" />

              <div className="bg-white rounded-lg">
                <div className="px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-medium text-gray-800">
                      {messages['assignment.detail.workLocation']}
                    </span>
                    <div className="flex items-center gap-2">
                      <AppSwitch
                        checked={sameAsInvoicing}
                        onChange={(checked) => setSameAsInvoicing(checked)}
                      />
                      <Text type="secondary" className="ms-2">
                        {messages['assignment.address.sameAsInvoicing']}
                      </Text>
                    </div>
                  </div>
                </div>
                {!sameAsInvoicing && (
                  <AppFormContainer
                    form={form}
                    layout="vertical"
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                    initialValues={defaultValues}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                    className="px-4 py-2"
                  >
                    <Row gutter={[16, 12]}>
                      <Col span={24}>
                        <Form.Item
                          label={messages['assignment.address.address1']}
                          name={['workLocation', 'address1']}
                        >
                          <AppInput
                            placeholder={
                              messages['assignment.address.enterAddress']
                            }
                            disabled={sameAsInvoicing}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item
                          label={messages['assignment.address.address2']}
                          name={['workLocation', 'address2']}
                        >
                          <AppInput
                            placeholder={
                              messages['assignment.address.enterAddress']
                            }
                            disabled={sameAsInvoicing}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item
                          label={messages['assignment.address.address3']}
                          name={['workLocation', 'address3']}
                        >
                          <AppInput
                            placeholder={
                              messages['assignment.address.enterAddress']
                            }
                            disabled={sameAsInvoicing}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24} md={14}>
                        <Form.Item
                          label={messages['assignment.address.city']}
                          name={['workLocation', 'city']}
                        >
                          <AppInput
                            placeholder={
                              messages['assignment.address.enterCity']
                            }
                            disabled={sameAsInvoicing}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24} md={10}>
                        <Form.Item
                          label={messages['assignment.address.county']}
                          name={['workLocation', 'county']}
                        >
                          <AppInput
                            placeholder={
                              messages['assignment.address.selectCounty']
                            }
                            disabled={sameAsInvoicing}
                            className="w-full !min-w-0"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24} md={14}>
                        <Form.Item
                          label={messages['assignment.address.postCode']}
                          name={['workLocation', 'postCode']}
                          rules={[
                            postCodeValidationRule(messages, payrollCountry),
                          ]}
                        >
                          <AppInput
                            placeholder={
                              messages['assignment.address.enterPostCode']
                            }
                            disabled={sameAsInvoicing}
                            upperCaseAll
                            onChange={(e) => {
                              form.setFieldValue(
                                ['workLocation', 'postCode'],
                                formatPostCode(e.target.value, payrollCountry),
                              );
                            }}
                            maxLength={8}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24} md={10}>
                        <Form.Item
                          label={messages['assignment.address.country']}
                          name={['workLocation', 'country']}
                        >
                          <AppSelect
                            placeholder={
                              messages['assignment.address.selectCountry']
                            }
                            options={countryList}
                            disabled={sameAsInvoicing}
                            className="w-full !min-w-0"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </AppFormContainer>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <AppRateForm
        className="my-4"
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        defaultValues={defaultValues}
        listHidden={['units', 'net', 'total']}
        hasBorder={false}
      />
    </div>
  );
};

export default AssignmentFormStacked;
